import React, { useEffect, useState } from 'react'
import { Box, CircularProgress, LinearProgress, Typography, Paper, Grid, Chip, TextField } from '@mui/material'
import { Container } from 'components'
import { getUnderwritersScoreHeaders, getUnderwritersScoreRiskScore } from 'api'
import { isCarrierLogin } from 'tools/auth'
import { reportException } from 'tools/logs'
import { formatDate, formatNumber, formatKm, formatDateTimeNow } from 'tools/helper'
import { HeaderData, RiskScoreData } from 'interfaces'
import strings from 'strings'
import styles from './underwritersScore.module.scss'
import EstimatedMiles from '../summaryReport/partial/estimatedMilesTotal'

const t = strings.UNDERWRITERS_SCORE

interface IUnderwritersScoreProps {
  print?: boolean
  showRiskBadges?: boolean
}

const UnderwritersScore: React.FC<IUnderwritersScoreProps> = (props) => {
  const { print, showRiskBadges = false } = props
  const [headerData, setHeaderData] = useState<HeaderData | null>(null)
  const [riskScoreData, setRiskScoreData] = useState<RiskScoreData | null>(null)
  const [isLoadingHeaders, setIsLoadingHeaders] = useState(true)
  const [isLoadingRiskScore, setIsLoadingRiskScore] = useState(true)
  const [policyCount, setPolicyCount] = useState<number>(1)

  const carrierId: any = isCarrierLogin()
    ? localStorage.getItem('companyId')
    : localStorage.getItem('carrierId') || '1'

  useEffect(() => {
    // Fetch both endpoints in parallel
    const fetchData = async () => {
      setIsLoadingHeaders(true)
      setIsLoadingRiskScore(true)

      try {
        const [headersResponse, riskScoreResponse] = await Promise.all([
          getUnderwritersScoreHeaders(carrierId),
          getUnderwritersScoreRiskScore(carrierId)
        ])
        
        setHeaderData(headersResponse as HeaderData)
        setRiskScoreData(riskScoreResponse as RiskScoreData)
      } catch (error) {
        reportException(error)
      } finally {
        setIsLoadingHeaders(false)
        setIsLoadingRiskScore(false)
      }
    }

    fetchData()
  }, [carrierId])

  useEffect(() => {
    if (headerData?.initialVehiclesWithinPolicy) {
      setPolicyCount(headerData.initialVehiclesWithinPolicy)
    }
  }, [headerData])

  const getRiskClassification = (score: number): {
    label: string
    color: string
    bgColor: string
  } => {
    if (!riskScoreData) return { label: '', color: '', bgColor: '' }

    const classifications = riskScoreData.underwritersScoreRiskClassification

    if (classifications.lowRisk && score >= classifications.lowRisk.lowerLimit && score <= classifications.lowRisk.upperLimit) {
      return { label: t.LOW_RISK, color: '#0072BC', bgColor: '#E3F2FD' }
    }
    if (classifications.mediumRisk && score >= classifications.mediumRisk.lowerLimit && score <= classifications.mediumRisk.upperLimit) {
      return { label: t.MEDIUM_RISK, color: '#0072BC', bgColor: '#E3F2FD' }
    }
    if (classifications.highRisk && score >= classifications.highRisk.lowerLimit && score <= classifications.highRisk.upperLimit) {
      return { label: t.HIGH_RISK, color: '#0072BC', bgColor: '#E3F2FD' }
    }
    if (classifications.veryHighRisk && score >= classifications.veryHighRisk.lowerLimit && score <= classifications.veryHighRisk.upperLimit) {
      return { label: t.VERY_HIGH_RISK, color: '#0072BC', bgColor: '#E3F2FD' }
    }
    return { label: t.VERY_HIGH_RISK, color: '#0072BC', bgColor: '#E3F2FD' }
  }

  const handlePolicyCountChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(event.target.value, 10)
    if (!isNaN(value) && value > 0) {
      setPolicyCount(value)
    }
  }

  const calculatedAvgKmPerPolicy = (): string => {
    if (!headerData?.averageDistancePerMonth || !policyCount) return '-'
    const avgPerPolicy = headerData.averageDistancePerMonth / policyCount
    return formatKm(avgPerPolicy, t.KM_SUFFIX)
  }

  const calculateDaysOfData = (): number => {
    if (!headerData?.monthsOfData) return 0
    const endDate = new Date()
    const startDate = new Date()
    startDate.setMonth(startDate.getMonth() - headerData.monthsOfData)
    
    const diffTime = Math.abs(endDate.getTime() - startDate.getTime())
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    
    return diffDays
  }

  const isLoading = isLoadingHeaders || isLoadingRiskScore

  const onboardedDate = localStorage.getItem('onboardingDate')
    ? formatDate(localStorage.getItem('onboardingDate') || '', false)
    : '-'

  const generatedDateTime = formatDateTimeNow()

  const riskScoreDate = riskScoreData?.riskScoreDate
    ? formatDate(riskScoreData.riskScoreDate, false)
    : '-'

  const finalRiskClassification = getRiskClassification(riskScoreData?.finalRiskScore || 0)
  const speedRiskClassification = getRiskClassification(riskScoreData?.speedRisk || 0)
  const mileageRiskClassification = getRiskClassification(riskScoreData?.mileageIndex || 0)
  const geographicRiskClassification = getRiskClassification(riskScoreData?.geographicIndex || 0)

  return (
    <Container className={styles.underwritersScoreContainer}>
      <Box sx={{ mt: 3 }}>
        {/* Header Section */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            border: '1px solid #E0E0E0',
            borderRadius: '8px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 3,
              pb: 2,
              borderBottom: '1px solid #E0E0E0',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
              <Typography variant="body1" color="text.primary">
                <strong>{t.ACTIVE_VEHICLES}:</strong>{' '}
                {isLoadingHeaders ? (
                  <CircularProgress size={14} sx={{ ml: 1 }} />
                ) : (
                  formatNumber(headerData?.activeVehicles)
                )}
              </Typography>
              <Typography variant="body1" color="text.primary">
                <strong>{t.ONBOARDED}:</strong> {onboardedDate}
              </Typography>
              <Typography variant="body1" color="text.primary">
                <strong>{headerData?.monthsOfData || 0} {t.MONTHS_OF_DATA}</strong>
              </Typography>
            </Box>
            <Box>
              <Typography variant="body1" color="text.primary">
                <strong>{t.GENERATED_ON} {generatedDateTime}</strong>
              </Typography>
            </Box>
          </Box>

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={2.4}>
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {t.AVG_KM_MONTH}
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                  {isLoadingHeaders ? (
                    <CircularProgress size={24} />
                  ) : (
                    headerData?.averageDistancePerMonth
                      ? `${(headerData.averageDistancePerMonth / 1000).toFixed(2)}${t.K_SUFFIX}`
                      : '-'
                  )}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {t.AVG_ACTIVE_VEHICLES_MONTH}
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                  {isLoadingHeaders ? (
                    <CircularProgress size={24} />
                  ) : (
                    formatNumber(headerData?.averageActiveVehiclesPerMonth)
                  )}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {t.AVG_KM_MONTH_VEHICLE}
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                  {isLoadingHeaders ? (
                    <CircularProgress size={24} />
                  ) : (
                    formatKm(headerData?.averageDistancePerMonthPerVehicle, t.KM_SUFFIX)
                  )}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {t.POLICY_COUNT}
                </Typography>
                <TextField
                  type="number"
                  value={policyCount}
                  onChange={handlePolicyCountChange}
                  disabled={isLoadingHeaders}
                  inputProps={{ min: 1, style: { textAlign: 'center' } }}
                  sx={{
                    width: '140px',
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '4px',
                      backgroundColor: '#FFFFFF',
                      height: '48px',
                    },
                    '& input': {
                      fontWeight: 'bold',
                      fontSize: '1.5rem',
                      padding: '8px 12px',
                    }
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={2.4}>
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {t.AVG_KM_MONTH_POLICY}
                </Typography>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
                  {isLoadingHeaders ? (
                    <CircularProgress size={24} />
                  ) : (
                    calculatedAvgKmPerPolicy()
                  )}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Paper>

        {/* Initial Fleet Assessment Score Section */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            border: '1px solid #E0E0E0',
            borderRadius: '8px',
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2 }} data-testid="underwriters-score-telematics-title">
            {t.NBI_TELEMATICS_SCORE_SECTION_TITLE}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t.CALCULATED_ON}{' '}
            {riskScoreDate}{' '}
            • {t.CONTAINS}{' '}
            {calculateDaysOfData() > 0
              ? `${calculateDaysOfData()} ${t.DAYS_OF_DATA}`
              : `- ${t.DAYS_OF_DATA}`}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 3, display: 'block', fontStyle: 'italic' }}>
            {t.MILEAGE_WEIGHTED_NOTE}
          </Typography>

          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }} data-testid="underwriters-score-telematics-value">
                {t.NBI_TELEMATICS_SCORE_VALUE}
              </Typography>
              {showRiskBadges && (
                <Chip
                  label={finalRiskClassification.label}
                  sx={{
                    backgroundColor: finalRiskClassification.bgColor,
                    color: finalRiskClassification.color,
                    fontWeight: 'bold',
                  }}
                />
              )}
            </Box>
            <Typography variant="h2" sx={{ fontWeight: 'bold', fontSize: '4rem' }}>
              {isLoadingRiskScore ? (
                <CircularProgress size={40} />
              ) : riskScoreData?.finalRiskScore ? (
                riskScoreData.finalRiskScore.toFixed(2)
              ) : (
                '-'
              )}
              <Typography
                component="span"
                variant="h4"
                color="text.secondary"
                sx={{ ml: 1 }}
              >
                {t.OUT_OF_100}
              </Typography>
            </Typography>
            <Box sx={{ mt: 2, maxWidth: '600px', mx: 'auto' }}>
              <LinearProgress
                variant="determinate"
                value={
                  riskScoreData?.finalRiskScore
                    ? Math.min(riskScoreData.finalRiskScore, 100)
                    : 0
                }
                sx={{
                  height: 20,
                  borderRadius: 1,
                  backgroundColor: '#E0E0E0',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: '#0072BC',
                  },
                }}
              />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
              <strong>{t.WEIGHTED_FORMULA}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t.WEIGHTED_FORMULA_TEXT} {'='} <span data-testid="underwriters-score-telematics-value-formula">{t.NBI_TELEMATICS_SCORE_VALUE}</span>
            </Typography>
          </Box>
        </Paper>

        {/* Current Fleet Metrics Section */}  
        <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 2 }}>
          {t.CURRENT_FLEET_METRICS}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          {t.CALCULATED_ON}{' '}
          {riskScoreDate}{' '}
          • {t.CONTAINS}{' '}
          {calculateDaysOfData() > 0
            ? `${calculateDaysOfData()} ${t.DAYS_OF_DATA}`
            : `- ${t.DAYS_OF_DATA}`}
        </Typography>

        <Grid container spacing={3}>
          {/* Speed Risk */}
          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                border: '1px solid #E0E0E0',
                borderRadius: '8px',
                height: '100%',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  {t.SPEED_INDEX}
                </Typography>
                {showRiskBadges && (
                  <Chip
                    label={speedRiskClassification.label}
                    sx={{
                      backgroundColor: speedRiskClassification.bgColor,
                      color: speedRiskClassification.color,
                      fontWeight: 'bold',
                    }}
                  />
                )}
              </Box>
              <Typography variant="h2" sx={{ fontWeight: 'bold', mb: 1, fontSize: '2.5rem' }}>
                {isLoadingRiskScore ? (
                  <CircularProgress size={30} />
                ) : riskScoreData?.speedRisk ? (
                  riskScoreData.speedRisk.toFixed(2)
                ) : (
                  '-'
                )}
                <Typography
                  component="span"
                  variant="h5"
                  color="text.secondary"
                  sx={{ ml: 1 }}
                >
                  {t.OUT_OF_100}
                </Typography>
              </Typography>
              <Box sx={{ mb: 2 }}>
                <LinearProgress
                  variant="determinate"
                  value={
                    riskScoreData?.speedRisk
                      ? Math.min(riskScoreData.speedRisk, 100)
                      : 0
                  }
                  sx={{
                    height: 12,
                    borderRadius: 1,
                    backgroundColor: '#E0E0E0',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: '#0072BC',
                    },
                  }}
                />
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                <strong>{t.NATIONAL_AVG}</strong>{' '}
                {riskScoreData?.speedRiskNationalAverage
                  ? riskScoreData.speedRiskNationalAverage.toFixed(2)
                  : '-'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                {t.SPEED_INDEX_DESCRIPTION}
              </Typography>
            </Paper>
          </Grid>

          {/* Mileage Index */}
          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                border: '1px solid #E0E0E0',
                borderRadius: '8px',
                height: '100%',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  {t.MILEAGE_INDEX}
                </Typography>
                {showRiskBadges && (
                  <Chip
                    label={mileageRiskClassification.label}
                    sx={{
                      backgroundColor: mileageRiskClassification.bgColor,
                      color: mileageRiskClassification.color,
                      fontWeight: 'bold',
                    }}
                  />
                )}
              </Box>
              <Typography variant="h2" sx={{ fontWeight: 'bold', mb: 1, fontSize: '2.5rem' }}>
                {isLoadingRiskScore ? (
                  <CircularProgress size={30} />
                ) : riskScoreData?.mileageIndex ? (
                  riskScoreData.mileageIndex.toFixed(2)
                ) : (
                  '-'
                )}
                <Typography
                  component="span"
                  variant="h5"
                  color="text.secondary"
                  sx={{ ml: 1 }}
                >
                  {t.OUT_OF_100}
                </Typography>
              </Typography>
              <Box sx={{ mb: 2 }}>
                <LinearProgress
                  variant="determinate"
                  value={
                    riskScoreData?.mileageIndex
                      ? Math.min(riskScoreData.mileageIndex, 100)
                      : 0
                  }
                  sx={{
                    height: 12,
                    borderRadius: 1,
                    backgroundColor: '#E0E0E0',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: '#0072BC',
                    },
                  }}
                />
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                <strong>{t.NATIONAL_AVG}</strong>{' '}
                {riskScoreData?.mileageIndexNationalAverage
                  ? riskScoreData.mileageIndexNationalAverage.toFixed(2)
                  : '-'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                {t.MILEAGE_INDEX_DESCRIPTION}
              </Typography>
            </Paper>
          </Grid>

          {/* Geographic Risk */}
          <Grid item xs={12} md={4}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                border: '1px solid #E0E0E0',
                borderRadius: '8px',
                height: '100%',
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                  {t.GEOGRAPHIC_INDEX}
                </Typography>
                {showRiskBadges && (
                  <Chip
                    label={geographicRiskClassification.label}
                    sx={{
                      backgroundColor: geographicRiskClassification.bgColor,
                      color: geographicRiskClassification.color,
                      fontWeight: 'bold',
                    }}
                  />
                )}
              </Box>
              <Typography variant="h2" sx={{ fontWeight: 'bold', mb: 1, fontSize: '2.5rem' }}>
                {isLoadingRiskScore ? (
                  <CircularProgress size={30} />
                ) : riskScoreData?.geographicIndex ? (
                  riskScoreData.geographicIndex.toFixed(2)
                ) : (
                  '-'
                )}
                <Typography
                  component="span"
                  variant="h5"
                  color="text.secondary"
                  sx={{ ml: 1 }}
                >
                  {t.OUT_OF_100}
                </Typography>
              </Typography>
              <Box sx={{ mb: 2 }}>
                <LinearProgress
                  variant="determinate"
                  value={
                    riskScoreData?.geographicIndex
                      ? Math.min(riskScoreData.geographicIndex, 100)
                      : 0
                  }
                  sx={{
                    height: 12,
                    borderRadius: 1,
                    backgroundColor: '#E0E0E0',
                    '& .MuiLinearProgress-bar': {
                      backgroundColor: '#0072BC',
                    },
                  }}
                />
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                <strong>{t.NATIONAL_AVG}</strong>{' '}
                {riskScoreData?.geographicIndexNationalAverage
                  ? riskScoreData.geographicIndexNationalAverage.toFixed(2)
                  : '-'}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                {t.GEOGRAPHIC_INDEX_DESCRIPTION}
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* How Risk Levels Work Section */}
        <Box sx={{ mt: 4, mb: 4, display: 'flex', justifyContent: 'center' }}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              border: '1px solid #E0E0E0',
              borderRadius: '8px',
              backgroundColor: '#F5F5F5',
              width: '85%',
            }}
          >
            <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2 }}>
              {t.HOW_RISK_LEVELS_WORK}
            </Typography>
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>{t.HIGHER_SCORES_BETTER}</strong>{t.HIGHER_SCORES_DESCRIPTION}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                <strong>{t.KM_PER_TRUCK_SCORING}</strong>{t.KM_PER_TRUCK_SCORING_VALUES}
              </Typography>
              <Typography variant="body2">
                <strong>{t.EACH_METRIC_WEIGHTED}</strong>{t.METRIC_WEIGHTS}
              </Typography>
            </Box>
          </Paper>
        </Box>

        {/* Estimated Kilometers Section */}
        <Box sx={{ mt: 4 }}>
          <EstimatedMiles useKilometers={true} />
        </Box>
      </Box>
    </Container>
  )
}

export default UnderwritersScore

